import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { type Crop, type PixelCrop } from 'react-image-crop'
import { ApiError } from '../../../lib/api'
import {
  compressAvatarFile,
  createDefaultAvatarCrop,
  readAvatarSourceDimensions,
  validateAvatarFile,
  type AvatarSourceDimensions,
} from '../../../lib/avatar-upload'
import { useAuthSession } from '../../auth/session'
import { isCurrentBusinessAgent } from '@/lib/auth-scope'
import {
  fetchOwnActivityLogs,
  fetchSettings,
  fetchSyncOverview,
  requestOwnEmailChange,
  requestOwnPasswordChange,
  resendOwnEmailVerification,
  updateBusinessSettings,
  updateOwnPassword,
  updateOwnSettings,
  uploadOwnAvatar,
  verifyOwnEmailCode,
} from '../api'
import { PageHeader, PageHeaderToolbar } from '@/components/app/PageHeader'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

import { Skeleton } from '@/components/ui/skeleton'

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { IacrmApiSettingsTab } from '../components/IacrmApiSettingsTab'
import { NotificationHistoryTable } from '../components/NotificationHistoryTable'
import { SecuritySessionsTable } from '../components/SecuritySessionsTable'
import { IacrmDocsTab } from '@/features/iacrm/components/IacrmDocsTab'

import { SettingsGroup } from '../components/SettingsGroup'
import { SettingsRow } from '../components/SettingsRow'
import { Switch } from '@/components/ui/switch'
import {
  NameDialog,
  AvatarDialog,
  EmailDialog,
  BusinessDialog,
  PasswordDialog,
} from '../dialogs'
import {
  BadgeCheck,
  Bell,
  BookOpen,
  Camera,
  Globe,
  Inbox,
  KeyRound,
  Lock,
  Mail,
  Pencil,
  Phone,
  RefreshCw,
  ShieldCheck,
  UserRound,
} from 'lucide-react'

type SettingsTabId = 'profile' | 'notifications' | 'security' | 'api' | 'api-docs'
type SettingsDialogId = 'name' | 'avatar' | 'email' | 'business' | 'password' | null
const settingsQueryKey = ['settings', 'profile']
const notificationStorageKey = 'frontend-settings-notification-preferences'

interface AvatarCropDraft extends AvatarSourceDimensions {
  file: File
  previewUrl: string
  crop: Crop
  completedCrop: PixelCrop | null
}

function getSettingsTabs(t: (key: string) => string): Array<{ id: SettingsTabId; label: string; icon?: ReactNode }> {
  return [
    { id: 'profile', label: t('settings.tabs.profile'), icon: <UserRound className="size-3.5" /> },
    { id: 'notifications', label: t('settings.tabs.notifications'), icon: <Bell className="size-3.5" /> },
    { id: 'security', label: t('settings.tabs.security'), icon: <Lock className="size-3.5" /> },
    { id: 'api', label: t('settings.tabs.api'), icon: <KeyRound className="size-3.5" /> },
    { id: 'api-docs', label: t('settings.tabs.apiDocs'), icon: <BookOpen className="size-3.5" /> },
  ]
}

function SettingsActionButton({
  label,
  icon,
  onClick,
}: {
  label: string
  icon: ReactNode
  onClick: () => void
}) {
  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="hidden gap-2 sm:inline-flex"
        onClick={onClick}
      >
        {icon}
        {label}
      </Button>
      <Button
        type="button"
        variant="outline"
        size="icon-sm"
        aria-label={label}
        className="sm:hidden"
        onClick={onClick}
      >
        {icon}
      </Button>
    </>
  )
}

interface NotificationPreferences {
  inbox: boolean
  security: boolean
  crm: boolean
}

type SettingsTone = 'neutral' | 'active' | 'success' | 'warning' | 'danger'

function settingsToneBadgeClass(tone: SettingsTone) {
  switch (tone) {
    case 'active':
      return 'border-transparent bg-sky-500/15 text-sky-900 dark:bg-sky-500/20 dark:text-sky-300'
    case 'success':
      return 'border-transparent bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300'
    case 'warning':
      return 'border-transparent bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300'
    case 'danger':
      return 'border-transparent bg-rose-500/15 text-rose-800 dark:bg-rose-500/20 dark:text-rose-300'
    default:
      return 'border-transparent bg-muted text-muted-foreground'
  }
}

function isValidEmailAddress(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

function splitDisplayName(displayName: string) {
  const parts = displayName.trim().split(/\s+/).filter(Boolean)

  return {
    firstName: parts[0] ?? '',
    lastName: parts.slice(1).join(' '),
  }
}

function readNotificationPreferences(): NotificationPreferences {
  try {
    const rawValue = window.localStorage.getItem(notificationStorageKey)
    if (!rawValue) {
      return { inbox: true, security: true, crm: true }
    }

    const parsed = JSON.parse(rawValue) as Partial<NotificationPreferences>
    return {
      inbox: parsed.inbox ?? true,
      security: parsed.security ?? true,
      crm: parsed.crm ?? true,
    }
  } catch {
    return { inbox: true, security: true, crm: true }
  }
}

function writeNotificationPreferences(preferences: NotificationPreferences) {
  window.localStorage.setItem(notificationStorageKey, JSON.stringify(preferences))
}

function SettingsPageSkeleton() {
  return (
    <section className="app-section">
      <PageHeader
        title={
          <div className="space-y-2">
            <Skeleton className="h-8 w-28" />
            <Skeleton className="h-4 w-72" />
          </div>
        }
        titleAddon={<Skeleton className="hidden h-7 w-24 rounded-full sm:block" />}
        right={
          <PageHeaderToolbar>
            <Skeleton className="h-8 w-full rounded-full sm:w-[320px]" />
          </PageHeaderToolbar>
        }
      />

      <div className="space-y-6">
        <div className="space-y-3">
          <Skeleton className="h-5 w-32" />
          <div className="rounded-[0.625rem] overflow-hidden">
            <div className="flex flex-col gap-1">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3.5 bg-card px-4 py-3.5">
                  <Skeleton className="size-9 shrink-0 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-4 w-40" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <Skeleton className="h-5 w-32" />
          <div className="rounded-[0.625rem] overflow-hidden">
            <div className="flex flex-col gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3.5 bg-card px-4 py-3.5">
                  <Skeleton className="size-9 shrink-0 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-4 w-40" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export function SettingsPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { user } = useAuthSession()
  const isAgent = isCurrentBusinessAgent(user)
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedTab = searchParams.get('tab')
  const [activeTab, setActiveTab] = useState<SettingsTabId>(() =>
    requestedTab === 'notifications' || requestedTab === 'security' || requestedTab === 'api' || requestedTab === 'api-docs'
      ? requestedTab
      : 'profile',
  )
  const [activeDialog, setActiveDialog] = useState<SettingsDialogId>(null)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [avatarCropDraft, setAvatarCropDraft] = useState<AvatarCropDraft | null>(null)
  const [pendingEmailInput, setPendingEmailInput] = useState('')
  const [emailCode, setEmailCode] = useState('')
  const [businessName, setBusinessName] = useState('')
  const [businessEmail, setBusinessEmail] = useState('')
  const [businessPhone, setBusinessPhone] = useState('')
  const [businessWebsite, setBusinessWebsite] = useState('')
  const [businessTimezone, setBusinessTimezone] = useState('')
  const [notificationPreferences, setNotificationPreferences] = useState<NotificationPreferences>({
    inbox: true,
    security: true,
    crm: true,
  })
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordStep, setPasswordStep] = useState<'password' | 'otp'>('password')
  const [passwordOtpCode, setPasswordOtpCode] = useState('')
  const settingsTabs = useMemo(() => getSettingsTabs(t), [t])
  const settingsQuery = useQuery({
    queryKey: settingsQueryKey,
    queryFn: fetchSettings,
  })

  const canViewSync = user?.permissions.includes('iacrm.sync-view') ?? false
  useEffect(() => {
    setActiveTab(
      requestedTab === 'notifications' || requestedTab === 'security' || requestedTab === 'api' || requestedTab === 'api-docs'
        ? requestedTab
        : 'profile',
    )
  }, [requestedTab])

  function handleTabChange(nextTab: SettingsTabId) {
    setActiveTab(nextTab)
    const nextParams = new URLSearchParams(searchParams)
    if (nextTab === 'profile') {
      nextParams.delete('tab')
    } else {
      nextParams.set('tab', nextTab)
    }
    setSearchParams(nextParams, { replace: true })
  }

  async function handleAvatarFileSelection(file: File) {
    validateAvatarFile(file)

    const dimensions = await readAvatarSourceDimensions(file)
    const defaultCrop = createDefaultAvatarCrop(dimensions)

    setAvatarCropDraft({
      file,
      previewUrl: URL.createObjectURL(file),
      width: dimensions.width,
      height: dimensions.height,
      crop: defaultCrop,
      completedCrop: null,
    })
  }

  function resetAvatarSelection() {
    setAvatarCropDraft(null)
  }

  function updateAvatarCrop(nextCrop: Crop, nextCompletedCrop: PixelCrop | null) {
    setAvatarCropDraft((current) => {
      if (current === null) {
        return current
      }

      return {
        ...current,
        crop: nextCrop,
        completedCrop: nextCompletedCrop,
      }
    })
  }

  const syncOverviewQuery = useQuery({
    queryKey: ['sync', 'overview'],
    queryFn: fetchSyncOverview,
    enabled: canViewSync,
  })

  useEffect(() => {
    setNotificationPreferences(readNotificationPreferences())
  }, [])

  useEffect(() => {
    if (!settingsQuery.data) {
      return
    }

    const payload = settingsQuery.data.data
    const identity = splitDisplayName(payload.user.display_name)
    setFirstName(identity.firstName)
    setLastName(identity.lastName)
    setPendingEmailInput(payload.user.pending_email ?? payload.user.email)
    setBusinessName(payload.business?.display_name ?? '')
    setBusinessEmail(payload.business?.contact_email ?? '')
    setBusinessPhone(payload.business?.contact_phone ?? '')
    setBusinessWebsite(payload.business?.website_url ?? '')
    setBusinessTimezone(payload.business?.timezone ?? '')
  }, [settingsQuery.data])

  useEffect(() => {
    return () => {
      if (avatarCropDraft?.previewUrl) {
        URL.revokeObjectURL(avatarCropDraft.previewUrl)
      }
    }
  }, [avatarCropDraft?.previewUrl])

  useEffect(() => {
    if (activeDialog !== 'avatar') {
      resetAvatarSelection()
    }
  }, [activeDialog])

  useEffect(() => {
    const url = new URL(window.location.href)
    if (url.searchParams.get('emailVerified') !== '1') {
      return
    }

    void (async () => {
      await queryClient.invalidateQueries({ queryKey: settingsQueryKey })
      await queryClient.invalidateQueries({ queryKey: ['auth', 'session'] })
      toast.success(t('settings.toasts.emailVerifiedSuccess'), { id: 'settings-toast' })
      url.searchParams.delete('emailVerified')
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)
    })()
  }, [queryClient])

  const settingsData = settingsQuery.data?.data ?? null
  const profileInitials = useMemo(() => {
    const source = settingsData?.user.display_name?.trim() || user?.display_name || 'HD Parrainage'

    return source
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('')
  }, [settingsData?.user.display_name, user?.display_name])
  const resolvedAvatarPreview = settingsData?.user.avatar_url?.trim() || null
  const pendingEmail = settingsData?.user.pending_email ?? null
  const emailVerified = Boolean(settingsData?.user.email_verified_at)
  const syncOverview = syncOverviewQuery.data?.data
  const syncAlertsValue = !canViewSync
    ? t('settings.sync.noVisibility')
    : syncOverviewQuery.isPending
      ? t('settings.sync.loading')
      : syncOverviewQuery.isError
        ? t('settings.sync.unavailable')
        : t('settings.sync.activeIssues', { count: syncOverview?.failed_jobs_total ?? 0 })
  const activityLogsQuery = useQuery({
    queryKey: ['settings', 'activity-logs'],
    queryFn: fetchOwnActivityLogs,
    enabled: activeTab === 'security',
    select: (res) => res.data,
  })
  const activityLogs = activityLogsQuery.data

  const saveNameMutation = useMutation({
    mutationFn: async (payload: { display_name: string; email: string; phone_number?: string | null; avatar_url?: string | null }) =>
      updateOwnSettings(payload),
    onSuccess: async (response) => {
      queryClient.setQueryData(settingsQueryKey, response)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['auth', 'session'] }),
        queryClient.refetchQueries({ queryKey: ['dashboard', 'business-summary'] }),
      ])
      setActiveDialog(null)
      toast.success(t('settings.toasts.nameUpdated'), { id: 'settings-toast' })
    },
    onError: (error) => toast.error((error as ApiError).message, { id: 'settings-toast' }),
  })

  const avatarMutation = useMutation({
    mutationFn: async () => {
      if (!settingsData || avatarCropDraft === null) {
        throw new Error('Select an image before saving.')
      }

      const compressedAvatar = await compressAvatarFile(avatarCropDraft.file, avatarCropDraft.completedCrop)
      const avatarResponse = await uploadOwnAvatar(compressedAvatar)

      return updateOwnSettings({
        display_name: settingsData.user.display_name,
        email: settingsData.user.email,
        phone_number: settingsData.user.phone_number ?? null,
        avatar_url: avatarResponse.data.user.avatar_url,
      })
    },
    onSuccess: async (response) => {
      queryClient.setQueryData(settingsQueryKey, response)
      queryClient.setQueryData(['auth', 'session'], (old: unknown) => {
        if (!old || typeof old !== 'object') return old
        return { ...old, avatar_url: response.data.user.avatar_url }
      })
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['auth', 'session'] }),
        queryClient.refetchQueries({ queryKey: ['dashboard', 'business-summary'] }),
        queryClient.refetchQueries({ queryKey: ['dashboard', 'agent-prospects'] }),
      ])
      setActiveDialog(null)
      resetAvatarSelection()
      toast.success(t('settings.toasts.avatarUpdated'), { id: 'settings-toast' })
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : t('settings.toasts.avatarUpdateFailed'), { id: 'settings-toast' }),
  })

  const emailChangeMutation = useMutation({
    mutationFn: async (payload: { email: string }) => requestOwnEmailChange(payload),
    onSuccess: async (response) => {
      queryClient.setQueryData(settingsQueryKey, response)
      await queryClient.invalidateQueries({ queryKey: ['auth', 'session'] })
      setEmailCode('')
      toast.success(t('settings.toasts.emailVerificationSent'), { id: 'settings-toast' })
    },
    onError: (error) => toast.error((error as ApiError).message, { id: 'settings-toast' }),
  })

  const emailVerifyMutation = useMutation({
    mutationFn: async (payload: { code: string }) => verifyOwnEmailCode(payload),
    onSuccess: async (response) => {
      queryClient.setQueryData(settingsQueryKey, response)
      await queryClient.invalidateQueries({ queryKey: ['auth', 'session'] })
      setEmailCode('')
      setActiveDialog(null)
      toast.success(t('settings.toasts.emailVerified'), { id: 'settings-toast' })
    },
    onError: (error) => toast.error((error as ApiError).message, { id: 'settings-toast' }),
  })

  const emailResendMutation = useMutation({
    mutationFn: resendOwnEmailVerification,
    onSuccess: () => {
      toast.success(t('settings.toasts.emailVerificationResent'), { id: 'settings-toast' })
    },
    onError: (error) => toast.error((error as ApiError).message, { id: 'settings-toast' }),
  })

  const businessMutation = useMutation({
    mutationFn: updateBusinessSettings,
    onSuccess: async (response) => {
      queryClient.setQueryData(settingsQueryKey, response)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['auth', 'session'] }),
        queryClient.invalidateQueries({ queryKey: ['businesses'] }),
        queryClient.refetchQueries({ queryKey: ['dashboard', 'business-summary'] }),
      ])
      setActiveDialog(null)
      toast.success(t('settings.toasts.businessUpdated'), { id: 'settings-toast' })
    },
    onError: (error) => toast.error((error as ApiError).message, { id: 'settings-toast' }),
  })

  const requestPasswordChangeMutation = useMutation({
    mutationFn: requestOwnPasswordChange,
    onSuccess: () => {
      setPasswordStep('otp')
      toast.success(t('settings.toasts.passwordCodeSent', 'Verification code sent to your email.'), { id: 'settings-toast' })
    },
    onError: (error) => toast.error((error as ApiError).message, { id: 'settings-toast' }),
  })

  const passwordMutation = useMutation({
    mutationFn: updateOwnPassword,
    onSuccess: () => {
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setPasswordOtpCode('')
      setPasswordStep('password')
      setActiveDialog(null)
      toast.success(t('settings.toasts.passwordUpdated'), { id: 'settings-toast' })
    },
    onError: (error) => toast.error((error as ApiError).message, { id: 'settings-toast' }),
  })

  if (settingsQuery.isPending) {
    return <SettingsPageSkeleton />
  }

  if (settingsQuery.isError || settingsData === null) {
    return (
      <article className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        {((settingsQuery.error as ApiError | undefined)?.message) ?? t('settings.errors.loadFailed')}
      </article>
    )
  }

  const fullName = `${firstName} ${lastName}`.trim()
  const normalizedBusinessEmail = businessEmail.trim()
  const businessEmailIsValid =
    normalizedBusinessEmail.length === 0 || isValidEmailAddress(normalizedBusinessEmail)

  return (
    <section className="app-section">
      <PageHeader
        title={t('settings.title')}
        right={
          <PageHeaderToolbar>
            <Tabs value={activeTab} onValueChange={(value) => handleTabChange(value as SettingsTabId)}>
              <TabsList>
                {settingsTabs
                  .filter((tab) => tab.id !== 'api' || canViewSync)
                  .map((tab) => (
                    <TabsTrigger key={tab.id} value={tab.id}>
                      <span className="inline-flex items-center gap-1.5">
                        {tab.icon}
                        <span>{tab.label}</span>
                      </span>
                    </TabsTrigger>
                  ))}
              </TabsList>
            </Tabs>
          </PageHeaderToolbar>
        }
      />

      {activeTab === 'profile' ? (
        <div className="space-y-6">
          <SettingsGroup label={t('settings.profile.personalIdentity')}>
            <SettingsRow
              data-onboarding-target="avatar"
              media={
                <div className="relative">
                  <Avatar className="size-12 border border-border/40 bg-background">
                    {resolvedAvatarPreview ? <AvatarImage src={resolvedAvatarPreview} alt={settingsData.user.display_name} /> : null}
                    <AvatarFallback className="bg-transparent text-muted-foreground">
                      <UserRound className="size-5" />
                    </AvatarFallback>
                  </Avatar>
                  <span className="absolute -bottom-0.5 -right-0.5 flex size-5 items-center justify-center rounded-full border border-border/40 bg-background text-muted-foreground">
                    <Camera className="size-2.5" />
                  </span>
                </div>
              }
              label={t('settings.profile.profileImage')}
              value={resolvedAvatarPreview ? t('settings.profile.avatarActive') : t('settings.profile.noAvatar')}
              description={t('settings.profile.avatarHint')}
              badges={
                <Badge
                  variant="outline"
                  size="sm"
                  className={cn('rounded-full', settingsToneBadgeClass(resolvedAvatarPreview ? 'success' : 'warning'))}
                >
                  {resolvedAvatarPreview ? t('settings.common.active') : t('settings.common.pending')}
                </Badge>
              }
              action={
                <SettingsActionButton
                  label={t('settings.profile.changeAvatar')}
                  icon={<Camera className="size-3.5" />}
                  onClick={() => setActiveDialog('avatar')}
                />
              }
            />
            <SettingsRow
              icon={<UserRound className="size-4" />}
              label={t('settings.profile.displayName')}
              value={settingsData.user.display_name}
              description={t('settings.profile.displayNameDescription')}
              action={
                <SettingsActionButton
                  label={t('common.edit')}
                  icon={<Pencil className="size-3.5" />}
                  onClick={() => setActiveDialog('name')}
                />
              }
            />
          </SettingsGroup>

          {!isAgent && <SettingsGroup
            data-onboarding-target="identity"
            label={
              <div className="flex items-center gap-2">
                <span>{t('settings.business.title')}</span>
                {settingsData.business?.last_synced_at && (
                  <Badge variant="secondary" className="text-xs">
                    <RefreshCw className="mr-1 h-3 w-3" />
                    {t('settings.business.synced', { date: new Date(settingsData.business.last_synced_at).toLocaleDateString() })}
                  </Badge>
                )}
              </div>
            }
            action={
              settingsData.permissions.can_update_business ? (
                <SettingsActionButton
                  label={t('settings.business.editIdentity')}
                  icon={<Pencil className="size-3.5" />}
                  onClick={() => setActiveDialog('business')}
                />
              ) : null
            }
          >
            {settingsData.business ? (
              <>
                <SettingsRow
                  media={
                    <Avatar className="size-9 rounded-md">
                      <AvatarImage src={settingsData.business.logo_url ?? undefined} alt={settingsData.business.display_name} className="object-contain" />
                      <AvatarFallback className="rounded-md text-xs font-semibold">
                        {settingsData.business.display_name.slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  }
                  label={t('settings.business.logo')}
                  value={settingsData.business.logo_url ? t('settings.business.logoSet') : t('settings.business.noLogo')}
                  badges={
                    <Badge variant="outline" size="sm" className={cn('rounded-full', settingsToneBadgeClass(settingsData.business.logo_url ? 'success' : 'warning'))}>
                      {settingsData.business.logo_url ? t('settings.common.active') : t('settings.common.missing')}
                    </Badge>
                  }
                  description={t('settings.business.logoDescription')}
                />
                <SettingsRow
                  icon={<BadgeCheck className="size-4" />}
                  label={t('settings.business.displayName')}
                  value={settingsData.business.display_name}
                  badges={
                    <Badge variant="outline" size="sm" className={cn('rounded-full', settingsToneBadgeClass('active'))}>
                      {t('settings.common.active')}
                    </Badge>
                  }
                  description={t('settings.business.businessDescription', {
                    legalName: settingsData.business.legal_name,
                    slug: settingsData.business.slug,
                    currency: settingsData.business.currency_code,
                    timezone: settingsData.business.timezone,
                  })}
                />
                <SettingsRow
                  icon={<Mail className="size-4" />}
                  label={t('settings.business.contactEmail')}
                  value={settingsData.business.contact_email ?? t('settings.common.notSet')}
                  badges={
                    <Badge
                      variant="outline"
                      size="sm"
                      className={cn('rounded-full', settingsToneBadgeClass(settingsData.business.contact_email ? 'success' : 'warning'))}
                    >
                      {settingsData.business.contact_email ? t('settings.common.set') : t('settings.common.missing')}
                    </Badge>
                  }
                  description={t('settings.business.contactEmailDescription')}
                />
                <SettingsRow
                  icon={<Phone className="size-4" />}
                  label={t('settings.business.contactPhone')}
                  value={settingsData.business.contact_phone ?? t('settings.common.notSet')}
                  badges={
                    <Badge
                      variant="outline"
                      size="sm"
                      className={cn('rounded-full', settingsToneBadgeClass(settingsData.business.contact_phone ? 'success' : 'warning'))}
                    >
                      {settingsData.business.contact_phone ? t('settings.common.set') : t('settings.common.missing')}
                    </Badge>
                  }
                  description={t('settings.business.contactPhoneDescription')}
                />
                <SettingsRow
                  icon={<Globe className="size-4" />}
                  label={t('settings.business.publicWebsite')}
                  value={settingsData.business.website_url ?? t('settings.common.notSet')}
                  badges={
                    <Badge
                      variant="outline"
                      size="sm"
                      className={cn('rounded-full', settingsToneBadgeClass(settingsData.business.website_url ? 'active' : 'warning'))}
                    >
                      {settingsData.business.website_url ? t('settings.common.online') : t('settings.common.missing')}
                    </Badge>
                  }
                  description={t('settings.business.publicWebsiteDescription')}
                />
              </>
            ) : (
              <div className="flex items-center gap-3.5 bg-card px-4 py-5">
                <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted/40 text-muted-foreground">
                  <BadgeCheck className="size-4" />
                </div>
                <p className="text-sm text-muted-foreground">{t('settings.business.noBusinessProfile')}</p>
              </div>
            )}
          </SettingsGroup>}
        </div>
      ) : null}

      {activeTab === 'notifications' ? (
        <div className="space-y-6">
          <SettingsGroup label={t('settings.notifications.title')}>
            <SettingsRow
              icon={<Inbox className="size-4" />}
              label={t('settings.notifications.options.inbox.label')}
              value={notificationPreferences.inbox ? t('settings.common.enabled') : t('settings.common.muted')}
              description={t('settings.notifications.options.inbox.description')}
              badges={
                <Badge
                  variant="outline"
                  size="sm"
                  className={cn('rounded-full', settingsToneBadgeClass(notificationPreferences.inbox ? 'success' : 'neutral'))}
                >
                  {notificationPreferences.inbox ? t('settings.common.active') : t('settings.common.mutedShort')}
                </Badge>
              }
              action={
                <Switch
                  checked={notificationPreferences.inbox}
                  onCheckedChange={(checked) => {
                    const next = { ...notificationPreferences, inbox: checked }
                    setNotificationPreferences(next)
                    writeNotificationPreferences(next)
                  }}
                />
              }
            />
            <SettingsRow
              icon={<ShieldCheck className="size-4" />}
              label={t('settings.notifications.options.security.label')}
              value={notificationPreferences.security ? t('settings.common.enabled') : t('settings.common.muted')}
              description={t('settings.notifications.options.security.description')}
              badges={
                <Badge
                  variant="outline"
                  size="sm"
                  className={cn('rounded-full', settingsToneBadgeClass(notificationPreferences.security ? 'success' : 'neutral'))}
                >
                  {notificationPreferences.security ? t('settings.common.active') : t('settings.common.mutedShort')}
                </Badge>
              }
              action={
                <Switch
                  checked={notificationPreferences.security}
                  onCheckedChange={(checked) => {
                    const next = { ...notificationPreferences, security: checked }
                    setNotificationPreferences(next)
                    writeNotificationPreferences(next)
                  }}
                />
              }
            />
            <SettingsRow
              icon={<RefreshCw className="size-4" />}
              label={t('settings.notifications.options.crm.label')}
              value={notificationPreferences.crm ? t('settings.common.enabled') : t('settings.common.muted')}
              description={syncAlertsValue}
              badges={
                <div className="flex flex-wrap items-center gap-2">
                  <Badge
                    variant="outline"
                    size="sm"
                    className={cn('rounded-full', settingsToneBadgeClass(notificationPreferences.crm ? 'active' : 'neutral'))}
                  >
                    {notificationPreferences.crm ? t('settings.common.active') : t('settings.common.mutedShort')}
                  </Badge>
                  {canViewSync ? (
                    <Badge
                      variant="outline"
                      size="sm"
                      className={cn(
                        'rounded-full',
                        settingsToneBadgeClass((syncOverview?.failed_jobs_total ?? 0) > 0 ? 'warning' : 'success'),
                      )}
                    >
                      {(syncOverview?.failed_jobs_total ?? 0) > 0 ? t('settings.sync.incidents') : t('settings.sync.healthy')}
                    </Badge>
                  ) : null}
                </div>
              }
              action={
                <Switch
                  checked={notificationPreferences.crm}
                  onCheckedChange={(checked) => {
                    const next = { ...notificationPreferences, crm: checked }
                    setNotificationPreferences(next)
                    writeNotificationPreferences(next)
                  }}
                />
              }
            />
          </SettingsGroup>

          <SettingsGroup label={t('settings.notifications.historyTitle')}>
            <NotificationHistoryTable />
          </SettingsGroup>
        </div>
      ) : null}

      {activeTab === 'security' ? (
        <div className="space-y-6">
          <SettingsGroup label={t('settings.security.title')}>
            <SettingsRow
              icon={<KeyRound className="size-4" />}
              label={t('settings.security.password')}
              value="•••••••••••••••"
              description={t('settings.security.passwordDescription')}
              badges={
                <Badge variant="outline" size="sm" className={cn('rounded-full', settingsToneBadgeClass('active'))}>
                  {t('settings.security.protected')}
                </Badge>
              }
              action={
                <SettingsActionButton
                  label={t('settings.security.changePassword')}
                  icon={<KeyRound className="size-3.5" />}
                  onClick={() => setActiveDialog('password')}
                />
              }
            />
            <SettingsRow
              icon={<Mail className="size-4" />}
              label={t('settings.profile.email')}
              value={settingsData.user.email}
              description={t('settings.profile.emailDescription')}
              badges={
                <Badge
                  variant="outline"
                  size="sm"
                  className={cn('rounded-full', settingsToneBadgeClass(emailVerified ? 'success' : 'warning'))}
                >
                  {emailVerified ? t('settings.common.verified') : t('settings.common.pending')}
                </Badge>
              }
              action={
                <SettingsActionButton
                  label={t('settings.profile.changeEmail')}
                  icon={<Pencil className="size-3.5" />}
                  onClick={() => setActiveDialog('email')}
                />
              }
            />
          </SettingsGroup>

          <SettingsGroup
            label={t('settings.security.latestSessions')}
            action={
              <Badge variant="outline" size="sm">
                {t('settings.security.entries', { count: activityLogs?.length ?? 0 })}
              </Badge>
            }
          >
            <SecuritySessionsTable
              logs={activityLogs}
              isLoading={activityLogsQuery.isPending}
            />
          </SettingsGroup>
        </div>
      ) : null}

      {activeTab === 'api' ? (
        <IacrmApiSettingsTab
          failedJobsValue={t('settings.sync.activeIssues', { count: syncOverview?.failed_jobs_total ?? 0 })}
        />
      ) : null}

      {activeTab === 'api-docs' ? (
        <IacrmDocsTab />
      ) : null}

      <NameDialog
        open={activeDialog === 'name'}
        onOpenChange={(open) => {
          if (!open) {
            const identity = splitDisplayName(settingsData.user.display_name)
            setFirstName(identity.firstName)
            setLastName(identity.lastName)
          }
          setActiveDialog(open ? 'name' : null)
        }}
        firstName={firstName}
        lastName={lastName}
        onFirstNameChange={setFirstName}
        onLastNameChange={setLastName}
        onSave={() =>
          saveNameMutation.mutate({
            display_name: fullName,
            email: settingsData.user.email,
            phone_number: settingsData.user.phone_number ?? null,
            avatar_url: settingsData.user.avatar_url,
          })
        }
        isPending={saveNameMutation.isPending}
        isValid={fullName.length > 0}
      />

      <AvatarDialog
        open={activeDialog === 'avatar'}
        onOpenChange={(open) => {
          if (!open) resetAvatarSelection()
          setActiveDialog(open ? 'avatar' : null)
        }}
        userDisplayName={settingsData.user.display_name}
        resolvedAvatarPreview={resolvedAvatarPreview}
        profileInitials={profileInitials}
        avatarCropDraft={avatarCropDraft}
        onFileSelect={handleAvatarFileSelection}
        onReset={resetAvatarSelection}
        onCropChange={updateAvatarCrop}
        onSave={() => avatarMutation.mutate()}
        isPending={avatarMutation.isPending}
      />

      <EmailDialog
        open={activeDialog === 'email'}
        onOpenChange={(open) => {
          if (!open) {
            setPendingEmailInput('')
            setEmailCode('')
          }
          setActiveDialog(open ? 'email' : null)
        }}
        currentEmail={settingsData.user.email}
        emailVerified={emailVerified}
        pendingEmail={pendingEmail}
        pendingEmailInput={pendingEmailInput}
        onPendingEmailChange={setPendingEmailInput}
        onSendVerification={async () => {
          await emailChangeMutation.mutateAsync({ email: pendingEmailInput.trim() })
        }}
        onResend={() => emailResendMutation.mutate()}
        onVerifyCode={(code) => emailVerifyMutation.mutate({ code })}
        emailCode={emailCode}
        onEmailCodeChange={setEmailCode}
        isSending={emailChangeMutation.isPending}
        isResending={emailResendMutation.isPending}
        isVerifying={emailVerifyMutation.isPending}
      />

      <BusinessDialog
        open={activeDialog === 'business'}
        onOpenChange={(open) => {
          if (!open) {
            setBusinessName(settingsData.business?.display_name ?? '')
            setBusinessEmail(settingsData.business?.contact_email ?? '')
            setBusinessPhone(settingsData.business?.contact_phone ?? '')
            setBusinessWebsite(settingsData.business?.website_url ?? '')
            setBusinessTimezone(settingsData.business?.timezone ?? '')
          }
          setActiveDialog(open ? 'business' : null)
        }}
        name={businessName}
        email={businessEmail}
        phone={businessPhone}
        website={businessWebsite}
        timezone={businessTimezone}
        onNameChange={setBusinessName}
        onEmailChange={setBusinessEmail}
        onPhoneChange={setBusinessPhone}
        onWebsiteChange={setBusinessWebsite}
        onTimezoneChange={setBusinessTimezone}
        onSave={() => {
          if (!businessEmailIsValid) {
            toast.error(t('settings.dialogs.business.invalidEmailError'), { id: 'settings-toast' })
            return
          }
          const normalizedWebsite = businessWebsite.trim()
          const websiteUrl =
            normalizedWebsite.length === 0
              ? null
              : /^https?:\/\//i.test(normalizedWebsite)
                ? normalizedWebsite
                : `https://${normalizedWebsite}`
          businessMutation.mutate({
            display_name: businessName.trim(),
            contact_email: normalizedBusinessEmail || null,
            contact_phone: businessPhone.trim() || null,
            website_url: websiteUrl,
            timezone: businessTimezone.trim() || null,
          })
        }}
        isPending={businessMutation.isPending}
        isValid={businessName.trim().length > 0 && businessEmailIsValid}
      />

      <PasswordDialog
        open={activeDialog === 'password'}
        onOpenChange={(open) => {
          if (!open) {
            setPasswordStep('password')
            setPasswordOtpCode('')
            setCurrentPassword('')
            setNewPassword('')
            setConfirmPassword('')
          }
          setActiveDialog(open ? 'password' : null)
        }}
        step={passwordStep}
        currentPassword={currentPassword}
        newPassword={newPassword}
        confirmPassword={confirmPassword}
        otpCode={passwordOtpCode}
        onCurrentPasswordChange={setCurrentPassword}
        onNewPasswordChange={setNewPassword}
        onConfirmPasswordChange={setConfirmPassword}
        onOtpChange={setPasswordOtpCode}
        onSendCode={async () => {
          await requestPasswordChangeMutation.mutateAsync({
            current_password: currentPassword,
            password: newPassword,
            password_confirmation: confirmPassword,
          })
        }}
        onVerifyCode={() =>
          passwordMutation.mutate({
            current_password: currentPassword,
            password: newPassword,
            password_confirmation: confirmPassword,
            verification_code: passwordOtpCode,
          })
        }
        onResendCode={() =>
          requestPasswordChangeMutation.mutate({
            current_password: currentPassword,
            password: newPassword,
            password_confirmation: confirmPassword,
          })
        }
        isSendingCode={requestPasswordChangeMutation.isPending}
        isVerifying={passwordMutation.isPending}
        isResending={requestPasswordChangeMutation.isPending}
      />
    </section>
  )
}

